import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Package, MagnifyingGlass as Search, ChatCircle as MessageCircle } from '@phosphor-icons/react';
import { Link } from '@/lib/router-compat';
import { useCountry } from '@/hooks/useCountry';
import { useTranslation } from '@/hooks/useTranslation';
import { COUNTRY_ROUTES } from '@/utils/countryRoutes';
import type { CountryCode } from '@/utils/countryConfig';

interface EmptyStateListingsProps {
  title?: string;
  description?: string;
  showAddVehicleButton?: boolean;
  showViewAllButton?: boolean;
  showContactButton?: boolean;
}

export const EmptyStateListings = ({ 
  title,
  description,
  showAddVehicleButton = true,
  showViewAllButton = true,
  showContactButton = true
}: EmptyStateListingsProps) => {
  const { country } = useCountry();
  const { t } = useTranslation();

  const defaultTitle = country === 'UK' ? 'No vehicles in this area' : 
                       country === 'DE' ? 'Keine Fahrzeuge hier' : 
                       'Niciun vehicul în zonă';
  
  const defaultDescription = country === 'UK' ? 'Try browsing all UK vehicles or adjust your filters.' :
                            country === 'DE' ? 'Versuchen Sie alle DE-Fahrzeuge oder Filter anpassen.' :
                            'Încearcă să vezi toate vehiculele sau ajustează filtrele.';

  return (
    <div className="flex items-center justify-center py-10">
      <Card className="max-w-lg w-full border border-border/60 bg-gradient-to-br from-card via-card to-primary/5 shadow-lg overflow-hidden relative">
        {/* Accent stripe top */}
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
        {/* Soft glow */}
        <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />

        <CardContent className="relative p-8 text-center space-y-5">
          {/* Illustrated icon — car silhouette + magnifying glass */}
          <div className="relative mx-auto h-28 w-40">
            <svg
              viewBox="0 0 160 110"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full"
              aria-hidden="true"
            >
              {/* Road shadow-sm */}
              <ellipse cx="80" cy="95" rx="55" ry="4" fill="hsl(var(--primary))" fillOpacity="0.08" />
              {/* Car body */}
              <path
                d="M30 70 L40 50 Q43 45 50 45 L100 45 Q108 45 113 51 L125 65 L130 65 Q135 65 135 70 L135 80 Q135 85 130 85 L125 85 Q123 92 116 92 Q109 92 107 85 L53 85 Q51 92 44 92 Q37 92 35 85 L30 85 Q25 85 25 80 L25 75 Q25 70 30 70 Z"
                fill="hsl(var(--primary))"
                fillOpacity="0.9"
              />
              {/* Windows */}
              <path d="M48 52 L55 52 L52 67 L42 67 Z" fill="hsl(var(--background))" fillOpacity="0.5" />
              <path d="M58 52 L95 52 Q100 52 103 58 L108 67 L55 67 Z" fill="hsl(var(--background))" fillOpacity="0.5" />
              {/* Wheels */}
              <circle cx="44" cy="85" r="6" fill="hsl(var(--primary))" />
              <circle cx="44" cy="85" r="2.5" fill="hsl(var(--background))" />
              <circle cx="116" cy="85" r="6" fill="hsl(var(--primary))" />
              <circle cx="116" cy="85" r="2.5" fill="hsl(var(--background))" />
              {/* Magnifying glass — accent */}
              <circle cx="110" cy="35" r="14" stroke="hsl(var(--accent))" strokeWidth="3" fill="hsl(var(--background))" />
              <circle cx="110" cy="35" r="14" fill="hsl(var(--accent))" fillOpacity="0.08" />
              <line x1="121" y1="46" x2="132" y2="58" stroke="hsl(var(--accent))" strokeWidth="3" strokeLinecap="round" />
              {/* Question mark inside glass */}
              <text x="110" y="42" textAnchor="middle" fontSize="18" fontWeight="700" fill="hsl(var(--accent))">?</text>
            </svg>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">{title || defaultTitle}</h2>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              {description || defaultDescription}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            {showViewAllButton && (
              <Button size="default" className="gap-2 shadow-md" asChild>
                <Link to={COUNTRY_ROUTES.vehicles(country as CountryCode)}>
                  <Search className="w-4 h-4" />
                  {country === 'UK' ? 'All UK vehicles' :
                   country === 'DE' ? 'Alle DE-Fahrzeuge' :
                   'Toate vehiculele'}
                </Link>
              </Button>
            )}

            {showAddVehicleButton && (
              <Button size="default" variant="outline" className="gap-2 border-primary/30 hover:bg-primary hover:text-primary-foreground" asChild>
                <Link to={country === 'UK' ? '/uk/create-listing-v2' : country === 'DE' ? '/de/create-listing-v2' : '/create-listing-v2'}>
                  <Package className="w-4 h-4" />
                  {country === 'UK' ? 'Add vehicle' : country === 'DE' ? 'Fahrzeug hinzufügen' : 'Adaugă vehicul'}
                </Link>
              </Button>
            )}

            {showContactButton && (
              <Button size="default" variant="ghost" className="gap-2" asChild>
                <Link to={COUNTRY_ROUTES.contact(country as CountryCode)}>
                  <MessageCircle className="w-4 h-4" />
                  {country === 'UK' ? 'Contact us' : country === 'DE' ? 'Kontakt' : 'Contact'}
                </Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};